import React, { useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { supabase } from '../../services/supabase';
import { ANALYTICS_EVENTS, trackEvent } from '../../utils/analytics';

interface ReportProblemModalProps {
  isOpen: boolean;
  onClose: () => void;
  userEmail?: string;
  userId?: string;
}

const ReportProblemModal: React.FC<ReportProblemModalProps> = ({
  isOpen,
  onClose,
  userEmail,
  userId,
}) => {
  const [issueTitle, setIssueTitle] = useState('');
  const [whatHappened, setWhatHappened] = useState('');
  const [expectedOutcome, setExpectedOutcome] = useState('');
  const [deviceBrowser, setDeviceBrowser] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const pageUrl = useMemo(() => {
    if (typeof window === 'undefined') return '';
    return window.location.href;
  }, []);

  const timestamp = useMemo(() => {
    return new Date().toLocaleString();
  }, []);

  useEffect(() => {
    if (!isOpen) return;

    trackEvent(ANALYTICS_EVENTS.REPORT_PROBLEM_OPENED, {
      page_url: pageUrl,
      has_user_email: Boolean(userEmail),
      has_user_id: Boolean(userId),
    });

    const defaultDeviceBrowser =
      typeof navigator !== 'undefined'
        ? `${navigator.platform || 'Unknown Platform'} | ${navigator.userAgent}`
        : 'Unavailable';

    setDeviceBrowser(defaultDeviceBrowser);

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);

    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = originalOverflow;
    };
  }, [isOpen, onClose, pageUrl, userEmail, userId]);

  const resetForm = () => {
    setIssueTitle('');
    setWhatHappened('');
    setExpectedOutcome('');
    setDeviceBrowser(
      typeof navigator !== 'undefined'
        ? `${navigator.platform || 'Unknown Platform'} | ${navigator.userAgent}`
        : 'Unavailable'
    );
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handleSubmit = async () => {
    if (!issueTitle.trim()) {
      toast.error('Please add a short title for the issue.');
      return;
    }

    if (!whatHappened.trim()) {
      toast.error('Please describe what happened.');
      return;
    }

    setIsSubmitting(true);

    try {
      const { data, error } = await supabase.functions.invoke('submit-support-request', {
        body: {
          type: 'bug',
          issueTitle: issueTitle.trim(),
          whatHappened: whatHappened.trim(),
          expectedOutcome: expectedOutcome.trim(),
          deviceBrowser: deviceBrowser.trim(),
          userEmail,
          userId,
          pageUrl,
        },
      });

      if (error) {
        throw error;
      }

      if (!data?.success) {
        throw new Error(data?.error || 'Failed to submit bug report.');
      }

      trackEvent(ANALYTICS_EVENTS.REPORT_PROBLEM_SUBMITTED, {
        page_url: pageUrl,
        has_user_email: Boolean(userEmail),
        has_user_id: Boolean(userId),
        issue_title_length: issueTitle.trim().length,
        what_happened_length: whatHappened.trim().length,
        expected_outcome_length: expectedOutcome.trim().length,
      });

      toast.success('Bug report submitted successfully.');
      handleClose();
    } catch (error) {
      trackEvent(ANALYTICS_EVENTS.REPORT_PROBLEM_FAILED, {
        page_url: pageUrl,
        has_user_email: Boolean(userEmail),
        has_user_id: Boolean(userId),
        error_message:
          error instanceof Error ? error.message : 'Failed to submit bug report.',
      });

      toast.error(error instanceof Error ? error.message : 'Failed to submit bug report.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-[100]"
      onClick={handleClose}
      aria-modal="true"
      role="dialog"
      aria-labelledby="report-problem-title"
    >
      <div className="flex min-h-full items-center justify-center overflow-y-auto p-4 sm:p-6">
        <div
          className="w-full max-w-lg rounded-2xl bg-white shadow-2xl max-h-[90vh] overflow-hidden"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="border-b border-gray-200 px-6 py-4">
            <div className="flex items-start justify-between gap-4">
              <div>
                <h2
                  id="report-problem-title"
                  className="text-xl font-semibold text-gray-900"
                >
                  Report a Problem
                </h2>
                <p className="mt-1 text-sm text-gray-600">
                  Tell us what went wrong and we'll send the report to our team to investigate.
                </p>
              </div>

              <button
                type="button"
                onClick={handleClose}
                className="rounded-md p-2 text-gray-500 transition hover:bg-gray-100 hover:text-gray-700"
                aria-label="Close report problem modal"
              >
                ✕
              </button>
            </div>
          </div>

          <div className="space-y-4 overflow-y-auto px-6 py-5">
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">
                Short title
              </label>
              <input
                type="text"
                value={issueTitle}
                onChange={(e) => setIssueTitle(e.target.value)}
                placeholder="Example: Contribution page won’t load"
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-900 outline-none transition focus:border-primary focus:ring-2 focus:ring-primary/20"
                maxLength={120}
              />
            </div>

            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">
                What happened?
              </label>
              <textarea
                value={whatHappened}
                onChange={(e) => setWhatHappened(e.target.value)}
                placeholder="Describe the problem you ran into."
                className="min-h-[120px] w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-900 outline-none transition focus:border-primary focus:ring-2 focus:ring-primary/20"
              />
            </div>

            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">
                What did you expect to happen?
              </label>
              <textarea
                value={expectedOutcome}
                onChange={(e) => setExpectedOutcome(e.target.value)}
                placeholder="Tell us what you expected instead."
                className="min-h-[100px] w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-900 outline-none transition focus:border-primary focus:ring-2 focus:ring-primary/20"
              />
            </div>

            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">
                Device / browser
              </label>
              <input
                type="text"
                value={deviceBrowser}
                onChange={(e) => setDeviceBrowser(e.target.value)}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-900 outline-none transition focus:border-primary focus:ring-2 focus:ring-primary/20"
              />
              <p className="mt-1 text-xs text-gray-500">
                We auto-fill this, but you can edit it if needed.
              </p>
            </div>

            <div className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-3 text-xs text-gray-600">
              <p>
                <span className="font-medium text-gray-800">Page URL:</span> {pageUrl}
              </p>
              <p className="mt-1">
                <span className="font-medium text-gray-800">Timestamp:</span> {timestamp}
              </p>
              {userEmail && (
                <p className="mt-1">
                  <span className="font-medium text-gray-800">User Email:</span> {userEmail}
                </p>
              )}
            </div>
          </div>

          <div className="flex items-center justify-end gap-3 border-t border-gray-200 px-6 py-4">
            <button
              onClick={handleClose}
              className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>

            <button
              onClick={handleSubmit}
              disabled={isSubmitting}
              className="rounded-lg bg-primary px-4 py-2 text-sm font-medium text-white hover:opacity-90 disabled:opacity-60"
            >
              {isSubmitting ? 'Preparing...' : 'Submit Report'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReportProblemModal;